// Angular imports

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http'; //Required by PrimeNg file upload control.

import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';

// app imports

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
// ngx-formly imports

import { FormlyModule } from '@ngx-formly/core';
import { FormlyPrimeNGModule } from '@ngx-formly/primeng';

// dspx-core imports

import {
  DspxCoreModule,
  MessagesModule,
  DspxBaseFormComponent,
  CheckboxFieldType,
  SwitchFieldType,
  ButtonFieldType,
  DateFieldType,
  DateTimeFieldType,
  FileUploadFieldType,
  AutoCompleteFieldType,
  AddressFieldType,
  MaskedInputFieldType,
  SelectButtonFieldType,
  TextAreaFieldType,
  MultiselectFieldType,
  ParagraphFieldType,
  PasswordFieldType,
  StepsFieldType,
  TabsFieldType,
  PageFieldType,
  PageRepeatingFieldType,
  SectionFieldType,
  SectionRepeatingFieldType,
  TableFieldType,
  FormNavigationFieldType,
  FormConfigurationService,
  dspxRequiredValidator,
  whitespaceValidator,
  sameAsValidator,
  differentToValidator,
  eitherMandatoryValidator,
  dateValidator,
  customValidator,
  dateLessThanValidator,
  dateLessThanOrEqualToValidator,
  dateGreaterThanValidator,
  dateGreaterThanOrEqualToValidator,
  numberGreaterThanValidator,
  numberLessThanValidator,
  verificationRequiredValidator,
  timeValidator,
  urlValidator,
  SearchBoxFieldType,
  FormSummaryFieldType,
  TimeFieldType,
  SingleCheckboxFieldType,
  RadioFieldType,
  DspxWrapperFormField,
  DspxWrapperContainer,
  DspxRadioWrapperFormField,
  PBIEmbeddedFieldType,
  HtmlHeadingFieldType,
  DspxWrapperFilter,
  FilterFieldType,
  CardSectionFieldType,
  CardSectionRepeatingFieldType,
  CardSetPageFieldType,
  CardSetPageRepeatingFieldType,
  CardSetTabsFieldType,
  CardSetTabsFlatFieldType,
  EditorFieldType,
  ImageFieldType,
  ExcelFileUploadType,
  RelatedEntitySummaryFieldType,
  DisplayFieldType,
  ListFieldType,
  MessageListFieldType,
  CardSectionOverviewFieldType,
  CardSetPageOverviewFieldType,
  CardSetPageInfoFieldType,
  CardSetPageListFieldType,
  InputFieldType,
  VerticalStepperFieldType,
  DocumentListFieldType,
  HeaderFieldType,
  DecimalFieldType,
  SelectFieldType
} from 'dspx-core';
import { PendingChangesGuard } from './guards/pending-changes.guard';
import { LandingComponent } from "./components/landing/landing.component";
import { StaticPageComponent } from './components/static-page/static-page.component';
import { IntegrityOfficerComponent } from './components/integrity-officer/integrity-officer.component';
import { IntegrityOfficerListComponent } from './components/integrity-officer-list/integrity-officer-list.component';
import { ReferenceDataListComponent } from './components/reference-data-list/reference-data-list.component';
import { ReferenceDataComponent } from './components/reference-data/reference-data.component';
import { CoiFormListComponent } from './components/coi-form-list/coi-form-list.component';
import { CoiFormComponent } from './components/coi-form/coi-form.component';
import { ApproverCoiFormComponent } from './components/approver-coi-form/approver-coi-form.component';
import { DopiFormListComponent } from './components/dopi-form-list/dopi-form-list.component';
import { DopiFormComponent } from './components/dopi-form/dopi-form.component';
import { ApproverDopiFormComponent } from './components/approver-dopi-form/approver-dopi-form.component';
import { GbhFormListComponent } from './components/gbh-form-list/gbh-form-list.component';
import { GbhFormComponent } from './components/gbh-form/gbh-form.component';
import { ApproverGbhFormComponent } from './components/approver-gbh-form/approver-gbh-form.component';


@NgModule({
  declarations: [
    AppComponent,
    LandingComponent,
    StaticPageComponent,
    IntegrityOfficerComponent,
    IntegrityOfficerListComponent,
    ReferenceDataListComponent,
    ReferenceDataComponent,
    CoiFormListComponent,
    CoiFormComponent,
    ApproverCoiFormComponent,
    DopiFormListComponent,
    DopiFormComponent,
    ApproverDopiFormComponent,
    GbhFormListComponent,
    GbhFormComponent,
    ApproverGbhFormComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    DspxCoreModule,
    MessagesModule,
    CardModule,
    ButtonModule,
    AvatarModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormlyPrimeNGModule,
    HttpClientModule,
    FormlyModule.forRoot({
      wrappers: [
        {name: 'dspx-form-field', component: DspxWrapperFormField},
        {name: 'dspx-container', component: DspxWrapperContainer},
        {name: 'dspx-radio-wrapper', component: DspxRadioWrapperFormField},
        {name: 'dspx-filter-wrapper', component: DspxWrapperFilter},
      ],
      types: [
        { name: 'input', component: InputFieldType, wrappers: ['dspx-form-field'] },
        { name: 'checkbox', wrappers: ['dspx-form-field'] },
        { name: 'radio', component: RadioFieldType, wrappers: ['dspx-radio-wrapper'] },
        { name: 'select', component: SelectFieldType, wrappers: ['dspx-form-field'] },
        { name: 'multicheckbox', component: CheckboxFieldType, wrappers: ['dspx-form-field'] },
        { name: 'switch', component: SwitchFieldType, wrappers: ['dspx-form-field'] },
        { name: 'button', component: ButtonFieldType },
        { name: 'date', component: DateFieldType, wrappers: ['dspx-form-field'] },
        { name: 'datetime', component: DateTimeFieldType, wrappers: ['dspx-form-field'] },
        { name: 'file', component: FileUploadFieldType, wrappers: ['dspx-form-field'] },
        { name: 'autocomplete', component: AutoCompleteFieldType, wrappers: ['dspx-form-field'] },
        { name: 'address', component: AddressFieldType, wrappers: ['dspx-form-field'] },
        { name: 'maskinput', component: MaskedInputFieldType, wrappers: ['dspx-form-field'] },
        { name: 'textarea', component: TextAreaFieldType, wrappers: ['dspx-form-field'] },
        { name: 'multiselect', component: MultiselectFieldType, wrappers: ['dspx-form-field'] },
        { name: 'selectbutton', component: SelectButtonFieldType, wrappers: ['dspx-form-field'] },
        { name: 'paragraph', component: ParagraphFieldType },
        { name: 'password', component: PasswordFieldType, wrappers: ['dspx-form-field'] },
        { name: 'decimal', component: DecimalFieldType, wrappers: ['dspx-form-field'] },
        { name: 'steps', component: StepsFieldType },
        { name: 'tabs', component: TabsFieldType },
        { name: 'page', component: PageFieldType },
        { name: 'page-repeating', component: PageRepeatingFieldType },
        { name: 'section', component: SectionFieldType },
        { name: 'section-repeating', component: SectionRepeatingFieldType },
        { name: 'formnavigation', component: FormNavigationFieldType },
        { name: 'data-grid', component: TableFieldType },
        { name: 'search', component: SearchBoxFieldType, wrappers: ['dspx-container'] },
        { name: 'summary-page', component: PageFieldType, wrappers: ['dspx-container'] },
        { name: 'summary', component: FormSummaryFieldType },
        { name: 'time', component: TimeFieldType, wrappers: ['dspx-form-field'] },
        { name: 'powerbi', component: PBIEmbeddedFieldType, wrappers: ['dspx-container'] },
        { name: 'heading', component: HtmlHeadingFieldType },
        { name: 'filter', component: FilterFieldType },
        { name: 'selectbutton', component: SelectButtonFieldType, wrappers: ['dspx-form-field'] },
        { name: 'editor', component: EditorFieldType, wrappers: ['dspx-form-field'] },
        { name: 'heading', component: HtmlHeadingFieldType },
        { name: 'image', component: ImageFieldType },
        { name: 'excel-file-upload', component: ExcelFileUploadType, wrappers: ['dspx-form-field'] },
        { name: 'related-entity-summary', component: RelatedEntitySummaryFieldType },
        { name: 'display', component: DisplayFieldType },
        { name: 'list', component: ListFieldType },
        { name: 'message-list', component: MessageListFieldType },
        { name: 'document-list', component: DocumentListFieldType },
        { name: 'header', component: HeaderFieldType },
        // 'card-set' layout
        { name: 'card-set-tabs', component: CardSetTabsFieldType },
        { name: 'card-set-tabs-flat', component: CardSetTabsFlatFieldType },
        { name: 'card-set-page', component: CardSetPageFieldType },
        { name: 'card-set-page-repeating', component: CardSetPageRepeatingFieldType },
        { name: 'card-set-page-overview', component: CardSetPageOverviewFieldType },
        { name: 'card-set-page-info', component: CardSetPageInfoFieldType },
        { name: 'card-set-page-list', component: CardSetPageListFieldType },
        { name: 'card-section', component: CardSectionFieldType },
        { name: 'card-section-repeating', component: CardSectionRepeatingFieldType },
        { name: 'card-section-overview', component: CardSectionOverviewFieldType },
        { name: 'vertical-stepper', component: VerticalStepperFieldType }
      ],
      validators: [
        { name: 'dspxRequired', validation: dspxRequiredValidator },
        { name: 'whitespace', validation: whitespaceValidator },
        { name: 'sameAs', validation: sameAsValidator },
        { name: 'differentTo', validation: differentToValidator },
        { name: 'eitherMandatory', validation: eitherMandatoryValidator },
        { name: 'anyOneMandatory', validation: eitherMandatoryValidator },
        { name: 'mandatoryPartialDate', validation: eitherMandatoryValidator },
        { name: 'date', validation: dateValidator },
        { name: 'dateLessThan', validation: dateLessThanValidator },
        { name: 'dateLessThanOrEqualTo', validation: dateLessThanOrEqualToValidator },
        { name: 'dateGreaterThan', validation: dateGreaterThanValidator },
        { name: 'dateGreaterThanOrEqualTo', validation: dateGreaterThanOrEqualToValidator, },
        { name: 'numberGreaterThan', validation: numberGreaterThanValidator },
        { name: 'numberLessThan', validation: numberLessThanValidator },
        { name: 'verificationRequired', validation: verificationRequiredValidator },
        { name: 'time', validation: timeValidator },
        { name: 'url', validation: urlValidator },
        { name: 'custom', validation: customValidator}
      ],
      extras: {
        resetFieldOnHide: true,
        checkExpressionOn: 'modelChange',
        lazyRender: true,
      },
    }),
  ],
  providers: [PendingChangesGuard],
  bootstrap: [AppComponent],
})
export class AppModule {
}
